import { PageBuilderBlogPost } from "@page-builder/ts/types";
import { adaptForComponent } from "./fieldAdapters";
import { DomainInfo } from "src/api";

interface CustomFieldConfig {
    taxonomyId: number;
    fieldCode: { label: string; value: string };
}

interface PostFieldConfig {
    fieldKey: string;
    fieldLabel: string;
}

interface DataSource {
    type: "static" | "customField" | "postField";
    static?: unknown;
    customField?: CustomFieldConfig;
    postField?: PostFieldConfig;
}

interface DataSourceWrapper {
    __dataSource: DataSource;
    [key: string]: unknown;
}

function tryParseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

function isDataSourceWrapper(value: unknown): value is DataSourceWrapper {
    return (
        typeof value === "object" &&
        value !== null &&
        "__dataSource" in (value as Record<string, unknown>)
    );
}

function resolveCustomField(
    config: CustomFieldConfig | null | undefined,
    post: PageBuilderBlogPost,
    componentId: string,
    propKey: string,
    domainInfo?: DomainInfo,
): unknown {
    const postTaxonomies = post?.taxonomyFieldGroups || [];
    const categoryTaxonomies =
        domainInfo?.categories?.flatMap((cat) => cat?.taxonomyFieldGroups) ||
        [];
    const offerTaxonomies =
        domainInfo?.defaultOffers?.flatMap(
            (offer) => offer?.taxonomyFieldGroups,
        ) || [];
    const taxonomies = [
        ...postTaxonomies,
        ...categoryTaxonomies,
        ...offerTaxonomies,
    ];
    if (!config || !taxonomies) return null;
    const fieldCode = config.fieldCode?.value;
    const fieldTaxonomyId = config?.taxonomyId;
    if (!fieldCode) return null;
    for (const taxonomy of taxonomies) {
        const field = taxonomy?.customFields?.find(
            (f) =>
                f.fieldCodeName === fieldCode &&
                f.taxonomyId === fieldTaxonomyId,
        );
        if (field) {
            const parsed = tryParseJson(field?.fieldValue);
            // Adapt based on component AND prop - returns unchanged if not matched
            return adaptForComponent(componentId, propKey, parsed);
        }
    }
    return null;
}

function formatPostField(fieldKey: string, value: string): string {
    if (fieldKey === "title") {
        return `<h1>${value}</h1>`;
    }
    return value;
}

function resolvePostField(
    fieldKey: string | undefined,
    post: PageBuilderBlogPost | null | undefined,
): unknown {
    if (!fieldKey || !post) return null;
    // Support dot-notation for nested keys e.g. 'category.name', 'url.url'
    const result = fieldKey
        .split(".")
        .reduce(
            (obj: Record<string, unknown> | null, key: string) =>
                obj !== null && typeof obj === "object"
                    ? (obj[key] as Record<string, unknown> | null)
                    : null,
            post as unknown as Record<string, unknown>,
        );

    return result != null
        ? tryParseJson(formatPostField(fieldKey, String(result)))
        : null;
}

/**
 * Resolves a single prop value that may contain a __dataSource wrapper.
 * Returns the resolved value for use in rendering.
 *
 * Handles:
 *   - Plain values (strings, numbers, booleans, objects) — returned as-is
 *   - { __dataSource: { type, static, customField, postField } } — resolved by type
 */
export function resolveDataSourceValue(
    propValue: unknown,
    post?: PageBuilderBlogPost | null,
    componentId?: string,
    propKey?: string,
    domainInfo?: DomainInfo,
): unknown {
    if (!isDataSourceWrapper(propValue)) return propValue;

    const ds = propValue.__dataSource;

    switch (ds.type) {
        case "static":
            return ds.static ?? null;
        case "customField":
            return resolveCustomField(
                ds.customField,
                post as PageBuilderBlogPost,
                componentId ?? "",
                propKey ?? "",
                domainInfo,
            );
        case "postField":
            return resolvePostField(ds.postField?.fieldKey, post);
        default:
            return null;
    }
}

/**
 * Walks all props for a component and resolves any data source values.
 * Non-data-source props are returned unchanged.
 */
export function resolveComponentProps(
    props: Record<string, unknown>,
    post?: PageBuilderBlogPost | null,
    componentId?: string,
    domainInfo?: DomainInfo,
): Record<string, unknown> {
    const resolved: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(props)) {
        resolved[key] = resolveDataSourceValue(
            value,
            post,
            componentId,
            key,
            domainInfo,
        );
    }
    return resolved;
}
